import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/api';
import { CreateAction, ActionList, FileUpload } from '../dtos/action';
import { accessToken, getAuthDetails } from '../utils/helper';
import Cookies from 'universal-cookie';
import { ApiResponseError } from '../dtos/common';

const cookies = new Cookies(null, { path: '/' });

type ActionListResponse = { data: ActionList[]; total: number; totalPages: number };
type ActionMessageResponse = { message: string };

const getApiErrorMessage = (error: unknown) => {
  const apiError = error as ApiResponseError;

  return apiError.response?.data?.message || 'Something Went Wrong!';
};

interface ActionState {
  actionList: ActionListResponse;
  completedFiles: { data: FileUpload[] };
  pendingAndFailedFiles: { data: FileUpload[] };
  loading: boolean;
  error: string | null;
  addActionResponse: ActionMessageResponse;
  addActionError: string | null;
  updateActionResponse: ActionMessageResponse;
  updateActionError: string | null;
  deleteActionResponse: ActionMessageResponse;
  deleteActionError: string | null;
}

const initialState: ActionState = {
  actionList: { data: [], total: 0, totalPages: 0 },
  completedFiles: { data: [] },
  pendingAndFailedFiles: { data: [] },
  addActionResponse: { message: '' },
  updateActionResponse: { message: '' },
  deleteActionResponse: { message: '' },
  loading: false,
  error: null,
  addActionError: null,
  updateActionError: null,
  deleteActionError: null,
};

export const getAllActionsTrigger = createAsyncThunk(
  'get/manage-actions',
  async (
    {
      page,
      limit,
      search,
      clientId,
      sortOrder,
      sortField,
    }: {
      page?: number;
      limit?: number;
      search?: string;
      clientId?: number;
      sortOrder?: string;
      sortField?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      // Clean the sortField to remove any malformed characters
      const cleanSortField = sortField ? sortField.split(':')[0] : undefined;
      
      const queryParams = new URLSearchParams();
      if (page) queryParams.append('page', page.toString());
      if (limit) queryParams.append('limit', limit.toString());
      if (search) queryParams.append('search', search);
      if (clientId) queryParams.append('clientId', clientId.toString());
      if (sortOrder) queryParams.append('sortOrder', sortOrder);
      if (cleanSortField) queryParams.append('sortField', cleanSortField);

      const response = await api.get(`/manage-actions?${queryParams}`, accessToken());
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const getPendingAndFailedFilesTrigger = createAsyncThunk(
  'action/getPendingAndFailedFiles',
  async () => {
    try {
      const response = await api.get(`/manage-actions/pending-failed/files`, accessToken());
      return response.data;
    } catch (error) {
      console.log(error);
      return { data: [] };
    }
  },
);

export const getCompletedFilesTrigger = createAsyncThunk('action/getCompletedFiles', async () => {
  try {
    const response = await api.get(`/manage-actions/completed/files`, accessToken());
    return response.data;
  } catch (error) {
    console.log(error);
    return { data: [] };
  }
});
export const addActionTrigger = createAsyncThunk(
  '/manage-actions/add',
  async (request: CreateAction, { rejectWithValue }) => {
    try {
      const response = await api.post<ActionMessageResponse>(
        `/manage-actions/add`,
        request,
        accessToken(),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const updateActionTrigger = createAsyncThunk(
  '/manage-actions/update/:id',
  async ({ id, request }: { id: number; request: CreateAction }, { rejectWithValue }) => {
    try {
      const response = await api.patch<ActionMessageResponse>(
        `/manage-actions/update/${id}`,
        request,
        accessToken(),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const deleteActionTrigger = createAsyncThunk(
  '/manage-actions/delete/:id',
  async (id: number | undefined, { rejectWithValue }) => {
    try {
      const response = await api.delete<ActionMessageResponse>(
        `/manage-actions/delete/${id}`,
        accessToken(),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const bulkUploadTrigger = createAsyncThunk(
  '/manage-actions/bulk-upload',
  async (request: FormData, { rejectWithValue }) => {
    try {
      const response = await api.post<ActionMessageResponse>(
        `/manage-actions/bulk-upload`,
        request,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${getAuthDetails().accessToken}`,
            'Content-Type': 'multipart/form-data',
            'x-csrf-token': cookies.get('XSRF-TOKEN'),
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

// Create slice
const actionSlice = createSlice({
  name: 'action',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllActionsTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllActionsTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.actionList = action.payload;
      })
      .addCase(getAllActionsTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || action.error.message || 'Failed to load actions';
      })
      .addCase(addActionTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(addActionTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.addActionResponse = action.payload;
      })
      .addCase(addActionTrigger.rejected, (state, action) => {
        state.loading = false;
        state.addActionError =
          (action.payload as string) || action.error.message || 'Failed to add action';
      })
      .addCase(updateActionTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateActionTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.updateActionResponse = action.payload;
      })
      .addCase(updateActionTrigger.rejected, (state, action) => {
        state.loading = false;
        state.updateActionError =
          (action.payload as string) || action.error.message || 'Failed to update action';
      })
      .addCase(deleteActionTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteActionTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteActionResponse = action.payload;
      })
      .addCase(deleteActionTrigger.rejected, (state, action) => {
        state.loading = false;
        state.deleteActionError =
          (action.payload as string) || action.error.message || 'Failed to delete action';
      })
      .addCase(getCompletedFilesTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCompletedFilesTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.completedFiles = action.payload;
      })
      .addCase(getCompletedFilesTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to get completed files';
      })
      .addCase(getPendingAndFailedFilesTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPendingAndFailedFilesTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingAndFailedFiles = action.payload;
      })
      .addCase(getPendingAndFailedFilesTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to get pending and failed files';
      });
  },
});

// Export the reducer to be added to the store
export default actionSlice.reducer;
